import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import type { InteractiveAnswer, PendingBlock, SSEMessage } from '@/_shared/types';

import { extractErrorMessage } from '../lib/extract-error';

interface UseInteractiveBlockResolverOptions {
  /** Session the block belongs to; resolveBlock no-ops when null. */
  sessionId: string | null;
  /** Optimistically append the resolved answer so the block clears even if the SSE stream is slow. */
  addMessage: (msg: SSEMessage) => void;
  /** Reconnect the SSE stream when the answer landed too late (block already resolved server-side). */
  reconnect: () => void;
}

interface UseInteractiveBlockResolverResult {
  resolveBlock: (block: PendingBlock, answer: InteractiveAnswer) => void;
  /** Block whose answer is in flight (locks its controls), or null. */
  submittingBlockId: string | null;
  /** Bumped when a block resolves so the composer refocuses once it re-enables (a11y). */
  composerFocusNonce: number;
}

/**
 * Shared resolver for interactive question/approval blocks (AI-411), used by both the main chat and the
 * audience chat. Submits via the session-generic chat.answerInteractive RPC, optimistically clears the
 * block, reconnects on a late answer, and bumps a composer-focus nonce on every settle path.
 */
export function useInteractiveBlockResolver({
  sessionId,
  addMessage,
  reconnect,
}: UseInteractiveBlockResolverOptions): UseInteractiveBlockResolverResult {
  const orpc = useOrpc();
  const answerInteractiveMutation = useMutation(orpc.chat.answerInteractive.mutationOptions({}));
  const [composerFocusNonce, setComposerFocusNonce] = useState(0);
  // Single-threaded turns: only one block is ever in flight, so derive the lock from the mutation itself.
  const submittingBlockId = answerInteractiveMutation.isPending
    ? (answerInteractiveMutation.variables?.blockId ?? null)
    : null;

  const resolveBlock = useCallback(
    (block: PendingBlock, answer: InteractiveAnswer): void => {
      if (!sessionId) return;
      answerInteractiveMutation.mutate(
        { id: sessionId, turnId: block.turnId, blockId: block.blockId, answer },
        {
          onSuccess: (res) => {
            if (res.resolved) {
              // Optimistically add the answer so the block clears (composer returns) even if the SSE
              // stream is slow or lost; the seq'd interactive_answer reconciles via the reducer.
              addMessage({ type: 'interactive_answer', blockId: block.blockId, answer });
            } else {
              // Already resolved server-side (timed out) or the turn ended before our answer landed —
              // reconnect so the authoritative terminal/answer replays and clears the block.
              reconnect();
              toast.error('This response arrived too late — the assistant already moved on.');
            }
            // Restore composer focus on every settle path. The host holds it while the card is still open.
            setComposerFocusNonce((n) => n + 1);
          },
          onError: (err) => {
            toast.error(extractErrorMessage(err));
            setComposerFocusNonce((n) => n + 1);
          },
        },
      );
    },
    [sessionId, answerInteractiveMutation, addMessage, reconnect],
  );

  return { resolveBlock, submittingBlockId, composerFocusNonce };
}
